/**
 * Module dependencies.
 */

import { Alert, AlertDescription, AlertTitle } from 'app/components/ui/alert';
import { AlertCircle, Landmark, Link2, RefreshCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from 'app/components/ui/alert-dialog';
import { BankAccountLink } from 'app/types/bank-account';
import { BankAccountSyncForm } from './bank-account-sync';
import { Button } from 'app/components/atoms/button/button';
import { ConnectWizard } from './components/connect-wizard';
import { ConnectionCard } from './components/connection-card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { Loading } from 'app/components/atoms/loading/loading';
import { SelectField } from 'app/components/atoms/forms/fields/select/select-field';
import { SubTitle } from 'app/components/atoms/sub-title/sub-title';
import { SubmitButton } from 'app/components/atoms/forms/submit-button/submit-button';
import { Title } from 'app/components/atoms/title/title';
import { useSetBreadcrumbTrail } from 'app/components/templates/dashboard/app-shell/breadcrumb-provider';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { formatCurrency } from 'app/core/utils/formatter';
import { requiredRule } from 'app/core/utils/field-rules';
import { searchOnInput } from 'app/core/utils/filter-utils';
import { uniqBy } from 'lodash';
import { useBankAccount } from 'app/hooks/requests/bank-accounts/use-bank-account';
import { useBankAccountCleanTransactions } from 'app/hooks/requests/bank-accounts/use-bank-account-clean-transactions';
import { useBankAccountConnectRebuild } from 'app/hooks/requests/bank-accounts/use-bank-account-rebuild';
import { useBankAccountDeleteAccount } from 'app/hooks/requests/bank-accounts/use-bank-account-delete-account';
import { useBankAccountDeleteConnection } from 'app/hooks/requests/bank-accounts/use-bank-account-delete-connection';
import { useBankAccountLinks } from 'app/hooks/requests/bank-accounts/use-bank-account-links';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, useState } from 'react';

/**
 * Export `BankAccountConnect` component.
 */

export function BankAccountConnect(): JSX.Element {
  const params = useParams();
  const [translate] = useTranslation();
  const [needRefresh, setNeedRefresh] = useState<any>();
  const { data: account, refetch: refresh } = useBankAccount(params.id ?? '');

  // Show the account name as the last breadcrumb crumb (… > foo).
  useSetBreadcrumbTrail(account?.name);

  const { mutateAsync: deleteConnection } = useBankAccountDeleteConnection();
  const { mutateAsync: deleteAccount } = useBankAccountDeleteAccount();
  const { mutateAsync: rebuild, isLoading: isRebuilding } = useBankAccountConnectRebuild();
  const { mutateAsync: cleanAllTransactions, isLoading: isCleanTransactionsLoading } =
    useBankAccountCleanTransactions();

  const { data: accounts, isLoading: isLoadingBankAccounts, refetch: accountsRefresh } = useBankAccountLinks();
  const { success } = useToast();
  const [syncIntegrationId, setSyncIntegrationId] = useState<string | null>(null);

  const refreshAll = useCallback(async () => {
    await accountsRefresh();
    await refresh();
  }, [accountsRefresh, refresh]);

  const handleDeleteConnection = useCallback(
    async (item: any, soft: boolean) => {
      try {
        await deleteConnection({ bankAccount: item.id, soft });
        await refreshAll();
      } catch (error) {
        console.log(error);
      }
    },
    [deleteConnection, refreshAll]
  );

  const accountOptions = useMemo(() => {
    return uniqBy(accounts, 'iban').map((item: BankAccountLink) => {
      return {
        label: item.name,
        value: item.id
      };
    });
  }, [accounts]);

  const { control, handleSubmit, watch, reset } = useForm({
    mode: 'all',
    values: {
      integration: account?.integration?.id
    }
  });

  const integrationValue = watch('integration');

  const handleDelete = useCallback(async () => {
    await deleteAccount({ integrationId: integrationValue });
    await refreshAll();

    reset();
  }, [deleteAccount, integrationValue, refreshAll, reset]);

  const onSubmit = useCallback(async (data: any) => {
    if (data.integration) {
      // Open the sync step as a modal (keeps the flow modal-based) instead of
      // navigating to the standalone sync page.
      setSyncIntegrationId(data.integration);
    }
  }, []);

  const schema = useMemo(() => {
    return {
      integration: requiredRule(translate)
    };
  }, [translate]);

  const haveTransactions = account?.transactions?.length;

  const handleReconnect = useCallback(async () => {
    location.href = needRefresh?.updateConsentLink;
  }, [needRefresh?.updateConsentLink]);

  const handleRebuild = useCallback(async () => {
    try {
      if (!account?.id) {
        return;
      }

      const result = await rebuild(account?.id);

      if (result.status !== 'UPDATED') {
        setNeedRefresh(result);
      }

      await refresh();
    } catch (error) {
      // Nothing to do.
    }
  }, [account?.id, rebuild, refresh]);

  const connectAccounts: any[] = (account as any)?.bankAccountConnectAccount ?? [];

  return (
    <div className={'w-full'}>
      <Title>{translate('bankAccounts.connect.title')}</Title>

      <Loading isLoading={isLoadingBankAccounts}>
        {!!account && (
          <div className={'mt-4 grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4'}>
            {[
              { label: translate('common.labels.name'), value: account?.name },
              { label: translate('common.labels.coin'), value: account?.coin?.name },
              { label: translate('common.labels.initialBalance'), value: formatCurrency(account?.initialBalance) },
              { label: translate('common.labels.balance'), value: formatCurrency(account?.balance) }
            ].map(stat => (
              <div key={stat.label}>
                <div className={'text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'}>
                  {stat.label}
                </div>

                <div className={'mt-0.5 text-sm font-semibold text-foreground'}>{stat.value}</div>
              </div>
            ))}
          </div>
        )}

        {haveTransactions ? (
          <Alert className={'mt-8'} variant={'destructive'}>
            <AlertCircle className={'h-4 w-4'} />

            <AlertTitle>{translate('bankAccounts.connect.invalidAccount.title')}</AlertTitle>

            <AlertDescription>{translate('bankAccounts.connect.invalidAccount.description')}</AlertDescription>
          </Alert>
        ) : (
          <div className={'mt-8 space-y-8'}>
            {/* Connections manager */}
            <section>
              <div className={'mb-4 flex items-center justify-between gap-4'}>
                <SubTitle className={'!mb-0 !text-[20px] font-semibold !text-[rgb(0,65,75)]'}>
                  {translate('bankAccounts.connections.title')}
                </SubTitle>

                {!account?.hasConnected && <ConnectWizard accountId={params.id} refresh={refreshAll} />}
              </div>

              {!!needRefresh && (
                <div
                  className={
                    'mb-4 flex items-center justify-between gap-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3'
                  }
                >
                  <p className={'text-sm text-amber-800'}>{translate('bankAccounts.connections.reconnectBanner')}</p>

                  <Button danger onClick={handleReconnect}>
                    {translate('common.actions.reconnect')}
                  </Button>
                </div>
              )}

              {connectAccounts.length > 0 ? (
                <div className={'space-y-2'}>
                  {connectAccounts.map((item: any) => (
                    <ConnectionCard
                      item={item}
                      key={item.id}
                      needsReconnect={!!needRefresh}
                      onRemove={soft => handleDeleteConnection(item, soft)}
                    />
                  ))}
                </div>
              ) : (
                <div className={'rounded-md border border-dashed border-border py-10 text-center'}>
                  <div
                    className={'mx-auto mb-3 grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary'}
                  >
                    <Landmark className={'h-5 w-5'} />
                  </div>

                  <p className={'text-sm font-medium'}>{translate('bankAccounts.connections.empty')}</p>

                  <p className={'mx-auto mt-1 max-w-[36ch] text-xs text-muted-foreground'}>
                    {translate('bankAccounts.connections.emptyDescription')}
                  </p>
                </div>
              )}

              {/* Account-level actions for an already-connected account */}
              {account?.hasConnected && !needRefresh && (
                <div className={'mt-4 flex flex-wrap gap-3'}>
                  <Button icon={<RefreshCw />} isLoading={isRebuilding} onClick={handleRebuild}>
                    {translate('bankAccounts.connections.syncNow')}
                  </Button>

                  <ConnectWizard
                    accountId={params.id}
                    buttonType={'primary'}
                    label={translate('common.actions.addNewConnection')}
                    refresh={refreshAll}
                  />
                </div>
              )}
            </section>

            {/* Finish connecting: the Open-Banking return step — the user came back
                from authorizing at their bank and now links one of the authorized
                accounts to this one and starts importing. */}
            {!account?.hasConnected && accountOptions.length > 0 && (
              <section className={'rounded-lg border border-primary/20 bg-primary/5 p-5'}>
                <div className={'mb-1 flex items-center gap-2'}>
                  <Link2 className={'h-[18px] w-[18px] shrink-0 text-primary'} />

                  <span className={'text-base font-semibold leading-none text-[rgb(0,65,75)]'}>
                    {translate('bankAccounts.connect.finishTitle')}
                  </span>
                </div>

                <p className={'mb-4 text-sm text-muted-foreground'}>
                  {translate('bankAccounts.connect.finishDescription')}
                </p>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className={'flex items-start gap-4'}>
                    <SelectField
                      className={'w-full min-w-[250px] overflow-hidden'}
                      control={control}
                      disabled={!!account?.integration}
                      filterOption={searchOnInput}
                      label={translate('common.labels.integration')}
                      name={'integration'}
                      options={accountOptions}
                      placeholder={translate('common.labels.integration')}
                      rules={schema.integration}
                      showSearch
                    />

                    <div className={'shrink-0'}>
                      <div aria-hidden className={'mb-1.5 h-5'} />

                      <SubmitButton className={'mt-0'} label={translate('common.actions.connect')} loading={false} />
                    </div>
                  </div>

                  {!!integrationValue && (
                    <Button className={'mt-3'} danger onClick={handleDelete}>
                      {translate('bankAccounts.connections.removeIntegration')}
                    </Button>
                  )}
                </form>
              </section>
            )}

            {/* Danger zone: wipe imported movements for this account */}
            {!account?.hasConnected && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button onClick={(event: any) => event?.stopPropagation()}>
                    {translate('common.actions.cleanAllTransactions')}
                  </Button>
                </AlertDialogTrigger>

                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{translate('common.actions.cleanAllTransactions')}</AlertDialogTitle>

                    <AlertDialogDescription>
                      {translate('bankAccounts.connections.cleanDescription')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  <AlertDialogFooter>
                    <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

                    <AlertDialogAction
                      className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
                      disabled={isCleanTransactionsLoading}
                      onClick={async (event: any) => {
                        event.stopPropagation();
                        try {
                          await cleanAllTransactions(account?.id);
                        } catch (error) {}
                      }}
                    >
                      {translate('common.actions.remove')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </Loading>

      <Dialog onOpenChange={value => !value && setSyncIntegrationId(null)} open={!!syncIntegrationId}>
        <DialogContent className={'sm:max-w-[520px]'}>
          <DialogHeader>
            <DialogTitle>{translate('bankAccounts.sync.title')}</DialogTitle>

            <DialogDescription>{translate('bankAccounts.sync.description')}</DialogDescription>
          </DialogHeader>

          {!!syncIntegrationId && (
            <BankAccountSyncForm
              accountId={params.id ?? ''}
              integrationId={syncIntegrationId}
              onDone={async () => {
                setSyncIntegrationId(null);
                reset();
                await refreshAll();
                success({ message: translate('bankAccounts.sync.done') });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
